<template>
	<div
		class="help-launcher"
		data-testid="help-assistant-launcher"
	>
		<sliding-sheet
			ref="sheet"
			:is-open="helpAssistantOpen"
			title="Help Assistant"
			width="760px"
			close-button-aria-label="Close help assistant"
			data-testid="help-assistant-sheet"
			role="dialog"
			aria-modal="true"
			aria-label="Help Assistant"
			@close="closeHelpAssistant"
		>
			<!--
				The sheet's own header slot, so Recent sits immediately left of the close control and
				the two read as one cluster. This matches the React panel, whose header carries the
				title and Recent with the Sheet's close floated over the corner (AB#27211).
			-->
			<template #header>
				<div class="help-launcher__header">
					<h3 class="help-launcher__title">
						Help Assistant
					</h3>
					<button
						type="button"
						data-testid="help-assistant-chats-toggle"
						:aria-expanded="chatsOpen ? 'true' : 'false'"
						aria-controls="help-assistant-chats-panel"
						class="help-launcher__recent"
						@click="chatsOpen = !chatsOpen"
					>
						<lucide-icon
							name="History"
							:size="14"
						/>
						Recent
					</button>
				</div>
			</template>

			<help-assistant-panel
				v-if="helpAssistantOpen"
				chrome="embedded"
				context="admin-portal"
				:suggested-questions="HERO_SUGGESTIONS_CORE"
				:chats-open="chatsOpen"
				@close-chats="chatsOpen = false"
			/>
		</sliding-sheet>
	</div>
</template>

<script>
import { LucideIcon } from '@syrenis/cassie-material-design'
import { SlidingSheet } from '../../component-library-wrapper.js'
import HelpAssistantPanel from './help-assistant-panel.vue'
import { HERO_SUGGESTIONS_CORE } from './utils/hero-suggestions.js'
import { helpAssistantOpen, closeHelpAssistant, helpAssistantHostMounted } from '../../../../../shared/state/help-assistant.js'

// Matches the focusable-elements query used by the React reference's local focus-trap hook
// (syrenis-frontends/apps/consent-configuration/src/hooks/useFocusTrap.ts) - the library
// `sliding-sheet.vue` provides neither Esc-to-close nor a focus trap nor focus-return-to-trigger
// (unlike the React launcher's Radix `Sheet`, which owns all three), so this component supplies
// the WCAG 2.2 keyboard-operability behavior itself.
const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Help-assistant sheet host (AB#26561 Task 9, entry point moved AB#27211): the shared
 * component-library `sliding-sheet` hosting `<help-assistant-panel>`. Mounted once, at app shell
 * level (`app.vue`), gated on `enableHelpAssistant`. It no longer renders a floating button: the
 * header's `help-header-button.vue` and the splash page's "Help Assistant" overview card drive the
 * same `helpAssistantOpen` singleton, so every entry point opens this one sheet instance. On close,
 * focus returns to the header button when it is present.
 */
const HEADER_BUTTON_SELECTOR = '[data-testid="help-assistant-header-button"]'
export default {
	name: 'help-launcher',
	components: {
		LucideIcon,
		SlidingSheet,
		HelpAssistantPanel
	},
	setup () {
		return {
			helpAssistantOpen,
			closeHelpAssistant
		}
	},
	data () {
		return {
			// Starter questions for the panel's empty state, matching what the React panels offer.
			// Module constants are not in template scope in Vue 2, so they are exposed here.
			HERO_SUGGESTIONS_CORE,
			// The Recent toggle lives in the sheet header here, so its state belongs to the host
			// rather than the panel. Reset on close so a reopened sheet starts on the conversation.
			chatsOpen: false
		}
	},
	watch: {
		helpAssistantOpen (isOpen) {
			if (isOpen) {
				document.addEventListener('keydown', this.handleKeydown)
				this.$nextTick(this.focusSheet)
			} else {
				this.chatsOpen = false
				document.removeEventListener('keydown', this.handleKeydown)
				this.$nextTick(() => document.querySelector(HEADER_BUTTON_SELECTOR)?.focus())
			}
		}
	},
	mounted () {
		// Tell the shared header a sheet host exists, so its help button renders (AB#27211).
		helpAssistantHostMounted.value = true
		// `helpAssistantOpen` is a module-level singleton that outlives this component (this
		// launcher itself unmounts/remounts as `helpLauncherVisible` in app.vue flips - e.g.
		// across a logout/login or a route change). The `watch` above only fires on a
		// true/false transition, so a remount while the sheet is already open would otherwise
		// come up with no Esc/focus-trap listener attached and no focus moved into the sheet.
		// Only sync the "opened" side here - not the "closed" else-branch - so a normal mount
		// with the sheet closed never steals focus onto the header button.
		if (this.helpAssistantOpen) {
			document.addEventListener('keydown', this.handleKeydown)
			this.$nextTick(this.focusSheet)
		}
	},
	beforeDestroy () {
		helpAssistantHostMounted.value = false
		document.removeEventListener('keydown', this.handleKeydown)
	},
	methods: {
		handleKeydown (event) {
			// Keys pressed in something Vuetify has hoisted out of the sheet (a dialog, a menu) are
			// that thing's to handle: the chat list's confirm-delete modal lives at the app root, and
			// Escape there must not tear the sheet down from underneath it. Scoped to those containers
			// rather than "anything outside the sheet": focus on <body> (after clicking transcript
			// text, say) still has to let Escape close the sheet.
			if (event.target instanceof Element && event.target.closest('.v-dialog__content, .v-menu__content')) return
			if (event.key === 'Escape') {
				this.closeHelpAssistant()
				return
			}
			if (event.key === 'Tab') this.trapFocus(event)
		},
		getFocusableElements () {
			const root = this.$refs.sheet?.$el
			if (!root || typeof root.querySelectorAll !== 'function') return []
			return Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR)).filter(el => el.offsetParent !== null)
		},
		focusSheet () {
			this.getFocusableElements()[0]?.focus()
		},
		trapFocus (event) {
			const focusable = this.getFocusableElements()
			if (focusable.length === 0) return
			const first = focusable[0]
			const last = focusable[focusable.length - 1]
			if (event.shiftKey && document.activeElement === first) {
				event.preventDefault()
				last.focus()
			} else if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault()
				first.focus()
			}
		}
	}
}
</script>

<style lang="scss" scoped>
/*
	The shared sliding-sheet fills its header with a background and pads its content by 20px. The
	React panel has neither: the header sits on the same surface as the transcript, separated only by
	a rule, and the panel runs to the sheet's edges. Scoped with ::v-deep so only the help assistant's
	sheet changes; every other feature using sliding-sheet keeps its own look (AB#27211).
*/
.help-launcher ::v-deep .sliding-sheet-header {
	background: none;
	// A fixed 48px row, as React's `h-12` header is. The sheet's own 12px top and bottom around a
	// 32px Recent button made it 56px, so the Vue sheet opened with a noticeably deeper header than
	// the same panel in the React portals.
	height: 48px;
	padding: 0 16px;
}

.help-launcher ::v-deep .sliding-sheet-content {
	padding: 0;
}

.help-launcher {
	&__header {
		display: flex;
		gap: 8px;
		align-items: center;
		justify-content: space-between;
	}

	// The header slot replaces the sheet's default heading, so it restates its type.
	&__title {
		margin: 0;
		// 14px/600, matching the React panel's `text-sm font-semibold` heading. 18px made the
		// sheet shout next to a transcript set at 13px.
		font-size: 0.875rem;
		font-weight: 600;
		color: var(--foreground, #4A5568);
	}

	// Mirrors the React panel's ghost Recent button: 12px muted label, 32px tall, muted hover fill.
	&__recent {
		display: flex;
		flex: none;
		gap: 6px;
		align-items: center;
		min-height: 32px;
		padding: 4px 8px;
		font-size: 0.8125rem;
		color: var(--muted-foreground, #666E7A);
		cursor: pointer;
		background: none;
		border: none;
		border-radius: var(--radius-sm, 4px);
		transition: background-color 0.15s ease, color 0.15s ease;

		&:hover {
			color: var(--foreground, #4A5568);
			background-color: var(--muted, #F5F5F5);
		}

		&:focus:not(:focus-visible) {
			outline: none;
		}

		&:focus-visible {
			outline: 2px solid var(--global-primary, #406A94);
			outline-offset: 2px;
		}
	}
}
</style>
